import {
  AD_INVENTORY_CODE,
  AD_MATERIAL_TYPE,
  AD_TRACKING_EVENT_KEY,
  Ad,
  AdTrackingEventKey,
  AdTrackingEventUrl,
} from "../types/ads";
import { getAdTrackingUrl, replaceAdTrackingPlaceholders } from "./ad-popup";

export const HOME_BANNER_LIMIT = 5;
export const HOME_BANNER_INTERVAL = 3000;

export interface HomeBannerAd {
  adId: string;
  imageUrl: string;
  imageMaterialId: string;
  landingUrl?: string;
  trackingEventUrls: AdTrackingEventUrl[];
}

const isWebUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

const getLandingUrl = (value?: string | null) => {
  if (typeof value !== "string" || !value) return undefined;
  const trimmed = value.trim();
  // Match the HTTP/TMAP destinations already opened by the EV native bridge.
  return isWebUrl(trimmed) || /^tmap:\/\//i.test(trimmed) ? trimmed : undefined;
};

export const normalizeHomeBannerAds = (ads?: Ad[] | null): HomeBannerAd[] => {
  if (!Array.isArray(ads)) return [];
  const seen = new Set<string>();

  return ads.reduce<HomeBannerAd[]>((result, ad) => {
    if (
      result.length >= HOME_BANNER_LIMIT ||
      !ad?.adId ||
      seen.has(ad.adId) ||
      ad.inventoryCode !== AD_INVENTORY_CODE.HOME_ROLLING_BANNER ||
      !Array.isArray(ad.materials)
    )
      return result;

    const images = ad.materials.filter(
      (material) =>
        typeof material?.type === "string" &&
        material.type.toLowerCase() === AD_MATERIAL_TYPE.IMAGE &&
        Boolean(material.id) &&
        typeof material.url === "string" &&
        isWebUrl(material.url)
    );
    const image =
      images.find(
        (material) =>
          material.sizeFormat?.w === 960 && material.sizeFormat?.h === 189
      ) || images[0];
    if (!image?.url) return result;

    seen.add(ad.adId);
    result.push({
      adId: ad.adId,
      imageUrl: image.url,
      imageMaterialId: image.id,
      landingUrl: getLandingUrl(ad.landingUrl),
      trackingEventUrls: Array.isArray(ad.trackingEventUrls)
        ? ad.trackingEventUrls.filter(
            (event) =>
              typeof event?.key === "string" && typeof event.value === "string"
          )
        : [],
    });
    return result;
  }, []);
};

export const getHomeBannerTrackingUrl = (
  ad: HomeBannerAd,
  key: AdTrackingEventKey,
  vimpressionId: string
) => {
  if (key === AD_TRACKING_EVENT_KEY.CLICK && !ad.landingUrl) return null;
  const value = getAdTrackingUrl(ad.trackingEventUrls, key);
  if (!value) return null;

  try {
    const url = new URL(value);
    const servingOrigin = new URL(process.env.REACT_APP_FRONTMAN_API_URL || "");
    // requestAdTracking includes the EUK header: send it only to our gateway.
    if (
      url.protocol !== "https:" ||
      url.origin !== servingOrigin.origin ||
      url.username ||
      url.password ||
      url.pathname !== `/ads/tracking/api/${key}`
    )
      return null;
  } catch {
    return null;
  }

  return replaceAdTrackingPlaceholders(
    value,
    vimpressionId,
    ad.imageMaterialId
  );
};
